/**
 * <DimensionLabel> — camera-facing text used by the Dimensions overlay
 * (and re-usable elsewhere). Wraps drei's `<Billboard>` + `<Text>` so the
 * label always reads horizontally regardless of camera orbit, and is
 * placed at a small +Z offset above the dimension tick so the text and
 * the marker don't z-fight.
 */

import React from 'react';
import { Billboard, Text } from '@react-three/drei';

/**
 * Default 3D-text font: Roboto Condensed (Latin, regular weight) served
 * from jsDelivr's @fontsource mirror. Pinned to a fixed major version so
 * the stack stays reproducible; the URL responds with a long
 * Cache-Control TTL so repeated reads come from the browser cache.
 *
 * `font` on troika-three-text expects an OTF / TTF / WOFF URL — NOT a CSS
 * font-family name. Consumers can override via `<DimensionLabel
 * fontFamily="https://…/whatever.woff" />` to swap in a brand font.
 */
export const DEFAULT_LABEL_FONT_URL =
  'https://cdn.jsdelivr.net/npm/@fontsource/roboto-condensed@5.2.7/files/roboto-condensed-latin-400-normal.woff' as const;

interface DimensionLabelProps {
  positionMm: [number, number, number];
  text: string;
  sizeMm?: number;
  color?: string;
  /** Vertical lift above `positionMm` (mm). Default: sizeMm. */
  liftMm?: number;
  /**
   * Font URL passed straight to troika-three-text. Defaults to
   * `DEFAULT_LABEL_FONT_URL` (Roboto Condensed). Pass any OTF / TTF / WOFF
   * URL to override.
   */
  fontFamily?: string;
}

export const DimensionLabel: React.FC<DimensionLabelProps> = ({
  positionMm,
  text,
  sizeMm = 30,
  color = '#88AAFF',
  liftMm,
  fontFamily,
}) => {
  const lift = liftMm ?? sizeMm;
  return (
    <Billboard
      position={[positionMm[0], positionMm[1], positionMm[2] + lift]}
      follow
      lockX={false}
      lockY={false}
      lockZ={false}
    >
      <Text
        fontSize={sizeMm}
        color={color}
        anchorX="center"
        anchorY="middle"
        // Thin outline — just enough contrast against busy backgrounds
        // (mover plates, GLB highlights) without the chunky 2D-game
        // halo effect a thicker stroke produces. ~1.2 % of font size,
        // softened by 50 % opacity.
        outlineWidth={sizeMm * 0.012}
        outlineColor="#000000"
        outlineOpacity={0.5}
        font={fontFamily ?? DEFAULT_LABEL_FONT_URL}
      >
        {text}
      </Text>
    </Billboard>
  );
};
